import { faArrowRight, faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, CheckboxList, HeadingItem, RadioButtonList } from 'components';
import { CheckBoxCard, MassSelection } from 'components/mass-verify-refuse';
import { ConfirmationPopup } from 'components/pop-up';
import { TripContext } from 'contexts';
import { ScrollView } from 'devextreme-react/scroll-view';
import notify from 'devextreme/ui/notify';
import { VerificationType } from 'enums';
import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useNavigation, useOrderItemService, useParamService } from 'services';
import { ReasonClass, Temperature } from 'services/enums';
import { CustomerPo, Order, OrderItem, ReconcileReason, RefusalReason } from 'services/types';
import { CheckBoxItem, SelectedItem } from 'types';
import './mass-refuse.scss';

export const MassRefusePage: FC = () => {

    const [isConfirming, setIsConfirming] = useState<boolean>(false)
    const [selectedReconcileReason, setSelectedReconcileReason] = useState<ReconcileReason>({} as ReconcileReason)
    const [selectedRefusalReason, setSelectedRefusalReason] = useState<RefusalReason>({} as RefusalReason)
    const [refusalOtherValue, setRefusalOtherValue] = useState<string>('')
    const [selectedRefusal, setSelectedRefusal] = useState<VerificationType>()
    const [selectedTemperatures, setSelectedTemperatures] = useState<CheckBoxItem[]>([])
    const [selectedPos, setSelectedPos] = useState<SelectedItem<CustomerPo>[]>([])

    const { getIntValue } = useParamService
    const { refuseItemsByPos, refuseItemsByTemps } = useOrderItemService()
    const { navigateBack } = useNavigation()

    const stopId = getIntValue('stopId')

    const tripContext = useContext(TripContext)
    const reconcileReasons: ReconcileReason[] = tripContext.reconcileReasons.toArray() ?? []
    const refusalReasons: RefusalReason[] = tripContext.refusalReasons.toArray() ?? []
    const stopOrders: Order[] = tripContext.orders.filter(o => o.stopId === stopId)
    const stopItems: OrderItem[] = tripContext.orderItems.filter(oi => stopOrders.some(o => o.id === oi.orderId))
    const pos: CustomerPo[] = []
    stopItems.forEach(item => {
        if (item.customerPoNumber && !pos.some(po => po.id === item.customerPoNumber)) {
            pos.push({
                id: item.customerPoNumber
            })
        }
    })

    const defaultTemperatures = (): CheckBoxItem[] => [
        { id: Temperature.Dry, isSelected: false, text: 'Dry', isEnable: true },
        { id: Temperature.Frozen, isSelected: false, text: 'Frozen', isEnable: true },
        { id: Temperature.Refrigerated, isSelected: false, text: 'Ref', isEnable: true }
    ]
    const defaultPos = (): SelectedItem<CustomerPo>[] => pos.map(po => { return { item: po, isSelected: false } })

    useEffect(() => {
        setSelectedTemperatures(defaultTemperatures)
        setSelectedPos(defaultPos())
        setSelectedReconcileReason({} as ReconcileReason)
        setSelectedRefusalReason({} as RefusalReason)
        setRefusalOtherValue('')
    }, [selectedRefusal])

    const isValidItemSelection = (): boolean => {
        let selectionList: SelectedItem<any>[] = []
        switch (selectedRefusal) {
            case VerificationType.Temp: { return selectedTemperatures.filter(item => item.isSelected).length > 0; }
            case VerificationType.PO: selectionList = selectedPos; break;
        }
        return selectionList.filter(item => item.isSelected).length > 0
    }


    const isReconcileReasonRefusal = (): boolean => selectedReconcileReason.id != null && refusalReasons.some(reason => reason.reconcileReasonId === selectedReconcileReason.id)

    const isValidSelection = (): boolean => isValidItemSelection() && (isReconcileReasonRefusal() ? (selectedReconcileReason.id != null && selectedRefusalReason.id != null) : selectedReconcileReason.id != null)

    const handleSelectedTempsChanged = (selectedTemp: string | number, isSelected: boolean) => {
        let tempIndex = selectedTemperatures.findIndex(temp => temp.id === selectedTemp)
        selectedTemperatures[tempIndex].isSelected = isSelected
        setSelectedTemperatures([...selectedTemperatures])
    }

    const handlePosSelected = (poNumbers: CustomerPo[]) => {
        let newSelection: SelectedItem<CustomerPo>[] = [...selectedPos]
        newSelection.forEach(po => {
            po.isSelected = poNumbers.some(selectedPo => selectedPo.id === po.item.id)
        })
        setSelectedPos(newSelection)
    }

    const handleConfirmRefusal = async (): Promise<void> => {
        if (selectedReconcileReason && stopId) {
            let count: number = 0
            switch (selectedRefusal) {
                case VerificationType.Temp: count = await refuseItemsByTemps(stopId, selectedTemperatures.filter(temp => temp.isSelected).map<Temperature>(temp => temp.id as Temperature), selectedReconcileReason, selectedRefusalReason, refusalOtherValue); break;
                case VerificationType.PO: count = await refuseItemsByPos(stopId, selectedPos.filter(po => po.isSelected).map(po => po.item), selectedReconcileReason, selectedRefusalReason, refusalOtherValue); break;
                default: break;
            }
            notify(`${count} ${count === 1 ? 'item has' : 'items have'} been refused!`, 'success', 5000)
            navigateBack()
        }
        setIsConfirming(false)
    }

    let content
    let confirmTypeText: string
    switch (selectedRefusal) {
        case VerificationType.Temp:
            content = (
                <div className={'card-material'}>
                    <HeadingItem text="Select Temperatures" />
                    <div className={'temp-selection-card'}>
                        <CheckboxList data={selectedTemperatures} onValueChanged={handleSelectedTempsChanged} />
                    </div>
                </div>
            );
            confirmTypeText = 'temperature';
            break;
        case VerificationType.PO:
            content = (
                <div className={'card-material'}>
                    <HeadingItem text={'Select POs'} />
                    <CheckBoxCard<CustomerPo> list={pos} keyExpr={'id'} displayExpr={'id'} selectedItems={selectedPos.filter(po => po.isSelected).map(po => po.item)} onValueChanged={handlePosSelected} />
                </div>
            );
            confirmTypeText = 'PO';
            break;
        default: content = <Fragment />; confirmTypeText = 'item'; break;
    }

    return (
        <Fragment>
            <Fragment>
                <ScrollView>
                    <div className={'mass-refuse-content'}>
                        <MassSelection onValueChanged={selectedOption => setSelectedRefusal(selectedOption)} />
                        {selectedRefusal != undefined &&
                            <div style={{ marginTop: '90px' }}>
                                {content}
                                {isValidItemSelection() &&
                                    <Fragment>
                                        <div className={'card-material'}>
                                            <HeadingItem text={'Select Reason'} />
                                            <div className={'mass-radio-list'}>
                                                <RadioButtonList<ReconcileReason>
                                                    data={reconcileReasons.filter(reason => reason.class != ReasonClass.Overage && reason.class != ReasonClass.Even)}
                                                    displayExpr={'description'}
                                                    valueExpr={'id'}
                                                    selectedItem={selectedReconcileReason}
                                                    handleRadioChecked={reasonId => setSelectedReconcileReason(reconcileReasons.find(reason => reason.id === reasonId) ?? {} as ReconcileReason)}
                                                />
                                            </div>
                                        </div>
                                        {isReconcileReasonRefusal() &&
                                            <div className={'card-material'}>
                                                <HeadingItem text={'Select Refusal Reason'} />
                                                <div className={'mass-radio-list'}>
                                                    <RadioButtonList<RefusalReason>
                                                        data={refusalReasons.filter(reason => reason.reconcileReasonId === selectedReconcileReason?.id)}
                                                        displayExpr={'description'}
                                                        valueExpr={'id'}
                                                        otherKeyValue={`99-${selectedReconcileReason?.id}`}
                                                        selectedItem={selectedRefusalReason}
                                                        otherValue={refusalOtherValue}
                                                        onOtherValueChanged={value => setRefusalOtherValue(value)}
                                                        handleRadioChecked={reasonId => setSelectedRefusalReason(refusalReasons.find(reason => reason.id === reasonId) ?? {} as RefusalReason)}
                                                    />
                                                </div>
                                            </div>
                                        }
                                    </Fragment>
                                }
                            </div>
                        }
                    </div>
                </ScrollView>
                <div className={'bottom-fix-action-row'}>
                    <ActionButton
                        enabled={isValidSelection()}
                        icon={faArrowRight}
                        buttonColor={'#76AB21'}
                        onClick={() => setIsConfirming(true)}
                        buttonText={'Confirm'}
                    />
                </div>
            </Fragment>
            <ConfirmationPopup
                isVisible={isConfirming}
                title="Confirm Verification"
                message={`Are you sure you want to refuse these ${confirmTypeText}s?`}
                button1={{ icon: faTimes }}
                button2={{ icon: faThumbsUp }}
                onClickButton1={() => setIsConfirming(false)}
                onClickButton2={handleConfirmRefusal}
            />
        </Fragment>
    )
}